'use client'

import {TFormProps} from "@/app/[locale]/common/components/Form/types";
import {Input} from "@/app/[locale]/common/components/Input/Input";
import {TextArea} from "@/app/[locale]/common/components/TextArea/TextArea";
import {Button} from "@/app/[locale]/common/components/Button/Button";
import s from './Form.module.scss';
import {FieldValues, SubmitHandler, useForm} from "react-hook-form";
import {ErrorMessage} from "@/app/[locale]/common/components/ErrorMessage/ErrorMessage";
import {
    EMAIL_VALIDATE,
    LABEL_MESSAGES,
    MESSAGES,
    NAME_VALIDATE,
    PHONE_VALIDATE
} from "../../constants/form";
import {Checkbox} from "@/app/[locale]/common/components/Checkbox/Checkbox";
import {useState} from "react";
import {Toast} from "@/app/[locale]/common/components/Toast/Toast";
import {GENERAL_URL} from "../../constants/app";
import {useTranslations} from "next-intl";
import {Link} from "@/i18n/routing";

type TResponseEmail = {
    status: boolean;
};

export const Form = (
    {
        version = 'desktop',
        buttonPosition = 'start',
        buttonSize = 'large',
        className
    }: TFormProps) => {
    const inputSize = version === 'desktop' ? 'medium' : 'small';
    const containerClass = `${s[`form_${version}Container`]}`;
    const buttonClass = `
    ${s[`form_${version}Button`]} 
    ${s[`form_${version}Button_${buttonPosition}`]}`;

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors } }
        = useForm({
        mode: 'onBlur'
    });

    const t = useTranslations('Form');
    const [ message, setMessage ] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

    const onSubmit: SubmitHandler<FieldValues> = async (data) => {
        setMessage('sending');
        const reqData = {
            name: data.name,
            email: data.email,
            phone: data.phone?.length ? data.phone : 'phone number not specified',
            text: data.text?.length ? data.text : 'message not specified',
        };

        try {
            const res = await fetch(`${GENERAL_URL}/email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(reqData),
            });

            const response: TResponseEmail = await res.json();
            if (response.status) {
                setMessage('success');
            } else {
                setMessage('error');
            }

        } catch (err) {
            console.log(err);
            setMessage('error');
        }
    }

    const handleCheckBoxChange = (checkBoxValue: boolean) => {
        setValue('checkBox', checkBoxValue, { shouldValidate: true });
    };

    return (
        <form className={`${s[`form_${version}`]} ${className}`} onSubmit={handleSubmit(onSubmit)}>
            <div className={containerClass}>
                <div className={`${s[`form_${version}InputContainer`]}`}>
                    <label className={`${s[`form_${version}Label`]}`}>
                        {t('labelName')}<span>*</span>
                    </label>

                    <Input
                        type={'text'}
                        label={'name'}
                        required={NAME_VALIDATE.required}
                        errorStyle={errors.name && typeof errors.name.message === 'string'}
                        textSize={inputSize}
                        placeholder={t('placeholderName')}
                        register={register}
                        minLengthSchema={NAME_VALIDATE.minLength}
                        validationPattern={NAME_VALIDATE.pattern}
                    />
                    {errors.name && typeof errors.name.message === 'string' &&
                        <ErrorMessage message={errors.name.message}/>}
                </div>
                <div className={`${s[`form_${version}InputContainer`]}`}>
                    <label className={`${s[`form_${version}Label`]}`}>
                        {t('labelPhone')}
                    </label>

                    <Input
                        type={'tel'}
                        label={'phone'}
                        textSize={inputSize}
                        errorStyle={errors.phone && typeof errors.phone.message === 'string'}
                        placeholder={t('placeholderPhone')}
                        register={register}
                        minLengthSchema={PHONE_VALIDATE.minLength}
                        maxLengthSchema={PHONE_VALIDATE.maxLength}
                        validationPattern={PHONE_VALIDATE.pattern}
                    />
                    {errors.phone && typeof errors.phone.message === 'string' &&
                        <ErrorMessage message={errors.phone?.message}/>}
                </div>
            </div>
            <div className={`${s[`form_${version}InputContainer`]} ${s[`form_${version}InputContainer_email`]}`}>
                <label className={`${s[`form_${version}Label`]}`}>
                    {LABEL_MESSAGES.EMAIL}<span>*</span>
                </label>

                <Input
                    type={'email'}
                    label={'email'}
                    errorStyle={errors.email && typeof errors.email.message === 'string'}
                    required={EMAIL_VALIDATE.required}
                    textSize={inputSize}
                    placeholder={'Email'}
                    register={register}
                    minLengthSchema={EMAIL_VALIDATE.minLength}
                    validationPattern={EMAIL_VALIDATE.pattern}
                />
                {errors.email && typeof errors.email.message === 'string' &&
                    <ErrorMessage message={errors.email?.message}/>}
            </div>
            <div className={`${s[`form_${version}TextareaContainer`]}`}>
                <p className={`${s[`form_${version}Label`]}`}>
                    {t('labelMessage')}
                </p>

                <TextArea
                    size={'medium'}
                    label={'text'}
                    textSize={inputSize}
                    register={register}
                    placeholder={t('placeholderMessage')}
                />
            </div>
            <div className={s[`form_${version}CheckBox`]}>
                <div className={s[`form_${version}CheckBoxContainer`]}>
                    <Checkbox handleCheckBoxChange={handleCheckBoxChange}/>
                    <label className={`${s[`form_${version}CheckBoxLabel`]}`}>
                        {t(`checkbox.startLabel`)}
                        <Link href={'/privacy-police'}>{t(`checkbox.link`)}</Link>
                        {t('checkbox.endLabel')}
                    </label>
                </div>

                <input
                    type="hidden"
                    {...register('checkBox', {
                        validate: value => value || 'Checkbox is required'
                    })}
                />
                {errors.checkBox && typeof errors.checkBox.message === 'string'
                    && (<ErrorMessage message={errors.checkBox.message}/>)}
            </div>
            <div className={buttonClass}>
                <Button
                    size={buttonSize}
                    type={'submit'}
                    disabled={message === 'sending'}
                    className={message === 'sending' ? `${s[`form_${version}_disabledButton`]}` : ''}
                >
                    {t('button')}
                </Button>
            </div>
            {
                message === 'success' &&
                <Toast
                    title={t('successTitleMessage')}
                    description={t('successDescriptionMessage')}
                    Icon={MESSAGES.SUCCESS.Icon}
                    isOpen={true}
                />
            }
            {
                message === 'error' &&
                <Toast
                    title={t('errorTitleMessage')}
                    description={t('errorDescriptionMessage')}
                    Icon={MESSAGES.ERROR.Icon}
                    isOpen={true}
                />
            }
        </form>
    );
};